import {LoadState} from 'enums';
import {IDeviceModel, IDeviceModelAPI, IFirmware, IFirmwareAPI, IManufacturer, IManufacturerAPI} from 'interfaces';
// Not using index import for services to avoid circular dependency
import {axiosServer} from './API';

// Holds manufacturers, models, and firmware versions for devices.
class _DeviceInfoService {
    public loadState = LoadState.Initial;
    public loadErrorMessage: string;

    // Hold all info, including deleted, for list views
    public allManufacturersById: {[id: string]: IManufacturer} = {};
    public allManufacturers: IManufacturer[] = [];
    public allModels: IDeviceModel[] = [];
    public allFirmwares: IFirmware[] = [];

    // Hold info, not including deleted, for select pickers
    public manufacturers: IManufacturer[] = [];
    public manufacturersById: {[id: string]: IManufacturer} = {};
    public modelsById: {[id: string]: IDeviceModel} = {};
    public firmwaresById: {[id: string]: IFirmware} = {};

    private loadPromise: Promise<any>;

    constructor() {}

    public loadIfNeeded(): Promise<any> {
        switch (this.loadState) {
            case LoadState.Loading:
            case LoadState.Success:
                // Load is in progress or has succeeded, return existing promise.
                break;
            case LoadState.Initial:
            case LoadState.Error:
                // Haven't started loading, create new promise.
                this.loadState = LoadState.Loading;
                this.loadPromise = this.getManufacturerMap();
                break;
        }
        return this.loadPromise;
    }

    public getManufacturerMap(): Promise<any> {

        // reset values on refresh
        this.allManufacturers = [];
        this.allModels = [];
        this.allFirmwares = [];
        this.allManufacturersById = {};

        this.manufacturers = [];
        this.manufacturersById = {};
        this.modelsById = {};
        this.firmwaresById = {};

        this.loadPromise = axiosServer.get<IManufacturerAPI[]>('/manufacturer-map')
            .then((response) => {
                this.loadState = LoadState.Success;
                
                // Filter out manufacturers that have been soft deleted and remap
                response.data.forEach(man => {
                    this.allManufacturers.push(this.mapAPIManToMan(man, true));
                    if (man.is_deleted) return;
                    this.manufacturers.push(this.mapAPIManToMan(man, false));
                });

                // We need to sort mans, models, and firmwares. We'll do natural sort, case insensitive.
                var collator = new Intl.Collator(undefined, {numeric: true, sensitivity: 'base'});
                this.manufacturers.sort((a, b) => {
                    return collator.compare(a.name, b.name);
                })

                // Sort all manufacturers and add to array to relate id and name for list views
                this.allManufacturers.sort((a, b) => {
                    return collator.compare(a.name, b.name);
                })
                for (const man of this.allManufacturers) {
                    this.allManufacturersById[man.id] = man;
                }

                // sort by manufacturer name
                this.allModels.sort((a, b) => {
                    return collator.compare(this.allManufacturersById[a.manufacturer_id].name, this.allManufacturersById[b.manufacturer_id].name);
                })
                this.allFirmwares.sort((a, b) => {
                    return collator.compare(this.allManufacturersById[a.manufacturer_id].name, this.allManufacturersById[b.manufacturer_id].name);
                })

                // Create maps by id for fast lookups, and sort models/firmwares.
                for (const man of this.manufacturers) {
                    this.manufacturersById[man.id] = man;

                    for (const model of man.models) {
                        this.modelsById[model.id] = model;
                    }

                    for (const f of man.firmwares) {
                        this.firmwaresById[f.id] = f;
                    }

                    // Sort models by name.
                    man.models.sort((a, b) => {
                        return collator.compare(a.name, b.name);
                    })

                    // Sort firmwares by version descending, so that version 1.10.0 comes before 1.9.9.
                    // To do descending, we'll just reverse the array after sorting.
                    man.firmwares.sort((a, b) => {
                        return collator.compare(a.firmware_version, b.firmware_version);
                    })
                    man.firmwares.reverse();
                }
            });
        this.loadPromise.catch(() => {
            this.loadState = LoadState.Error;
        });
        return this.loadPromise;
    }

    public getFirmwareVersion(id: number): Promise<IFirmware> {
        // Need to make sure info is loaded so mapping works.
        const infoPromise = DeviceInfoService.loadIfNeeded();
        const fvPromise = axiosServer.get<IFirmware>(`/firmware-versions/${id}`);
        fvPromise.catch((err) => {
            console.log('Error loading firmware version: ', err);
        })
        return Promise.all([infoPromise, fvPromise])
            .then(([deviceInfo, response]) => {
                return response.data;
            })
    }
    
    public createFirmwareVersion(fv: IFirmware): Promise<IFirmware> {
        return axiosServer.post<IFirmware>('/firmware-versions', fv)
            .then((response) => {
                this.loadState = LoadState.Error; // set as error to force reload of list
                return response.data;
            });
    }

    public deleteFirmwareVersion(id: number): Promise<any> {
        return axiosServer.delete<any>(`/firmware-versions/${id}`);
    }

    public getManufacturer(id: number): Promise<IManufacturer> {
        // Need to make sure info is loaded so mapping works.
        const infoPromise = DeviceInfoService.loadIfNeeded();
        const mPromise = axiosServer.get<IManufacturer>(`/manufacturers/${id}`);
        mPromise.catch((err) => {
            console.log('Error loading manufacturer: ', err);
        })
        return Promise.all([infoPromise, mPromise])
            .then(([deviceInfo, response]) => {
                return response.data;
            })
    }
    
    public createManufacturer(man: IManufacturer): Promise<IManufacturer> {
        return axiosServer.post<IManufacturer>('/manufacturers', man)
            .then((response) => {
                this.loadState = LoadState.Error; // set as error to force reload of list
                return response.data;
            });
    }

    public deleteManufacturer(id: number): Promise<any> {
        return axiosServer.delete<any>(`/manufacturers/${id}`);
    }

    public getModel(id: number): Promise<IDeviceModel> {
        // Need to make sure info is loaded so mapping works.
        const infoPromise = DeviceInfoService.loadIfNeeded();
        const mPromise = axiosServer.get<IDeviceModel>(`/models/${id}`);
        mPromise.catch((err) => {
            console.log('Error loading model: ', err);
        })
        return Promise.all([infoPromise, mPromise])
            .then(([deviceInfo, response]) => {
                return response.data;
            })
    }
    
    public createModel(man: IDeviceModel): Promise<IDeviceModel> {
        return axiosServer.post<IDeviceModel>('/models', man)
            .then((response) => {
                this.loadState = LoadState.Error; // set as error to force reload of list
                return response.data;
            });
    }

    public deleteModel(id: number): Promise<any> {
        return axiosServer.delete<any>(`/models/${id}`);
    }

    private mapAPIManToMan = (apiMan: IManufacturerAPI, includeAll: boolean): IManufacturer => {
        const id = apiMan.id;

        let newMan = {
            id: id,
            name: apiMan.name,
            models: [],
            firmwares: [],
            is_deleted: apiMan.is_deleted,
            is_deleted_at: apiMan.is_deleted_at && new Date(apiMan.is_deleted_at),
        }
        
        // Filter out models that have been soft deleted and remap
        apiMan.models.forEach(mod => {
            if (includeAll) this.allModels.push(this.mapAPIModelToModel(mod, id));
            if (mod.is_deleted) return;
            newMan.models.push(this.mapAPIModelToModel(mod, id));
        });

        // Filter out firmware versions that have been soft deleted and remap
        apiMan.firmwares.forEach(fv => {
            if (includeAll) this.allFirmwares.push(this.mapAPIFirmwareToFirmware(fv, id));
            if (fv.is_deleted) return;
            newMan.firmwares.push(this.mapAPIFirmwareToFirmware(fv, id));
        });

        return newMan;
    }

    private mapAPIModelToModel = (apiModel: IDeviceModelAPI, manufacturerId: number): IDeviceModel => {
        return {
            id: apiModel.id,
            manufacturer_id: manufacturerId,
            name: apiModel.name,
            is_deleted: apiModel.is_deleted,
            is_deleted_at: apiModel.is_deleted_at && new Date(apiModel.is_deleted_at),
        }
    }

    private mapAPIFirmwareToFirmware = (apiFirmware: IFirmwareAPI, manufacturerId: number): IFirmware => {
        return {
            id: apiFirmware.id,
            manufacturer_id: manufacturerId,
            firmware_version: apiFirmware.firmware_version,
            is_deleted: apiFirmware.is_deleted,
            is_deleted_at: apiFirmware.is_deleted_at && new Date(apiFirmware.is_deleted_at),
        }
    }
}

export const DeviceInfoService = new _DeviceInfoService();